import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { arrayToObject, take } from 'lib/array/arrayUtils'
import OfferCalendarDaysMonth from './OfferCalendarDaysMonth'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'

import {
  getMultiRoomCalendarDays,
  getMultiRoomDaysOtherAvailability,
  getSelectedOfferPageMultiRoomDay,
} from 'selectors/offerPage/multipleRoomBookingSelectors'
import type { MonthDayView } from 'selectors/offerPage/offerPageSelectors'
import { getCalendarDays, getDaysOtherAvailability, getSelectedOfferPageDay } from 'selectors/offerPage/offerPageSelectors'
import Heading from 'LuxKit/Typography/Heading'
import { useInView } from 'react-intersection-observer'
import { fadeIn } from 'components/utils/animations'
import OfferCalendarPriceBlurb from '../OfferCalendarPriceBlurb'
import useMountedEffect from 'hooks/useMountedEffect'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import { INTL_LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import { useIntl } from 'react-intl'

const Root = styled.div`
  width: 100%;
`

const Calendar = styled(VerticalSpacer)`
  animation: ${fadeIn} 0.2s;
`

interface Props {
  daysByMonth: Array<MonthDayView>
  className?: string
  onBackToMonth?: () => void
  initialMonth?: string
  room?: App.PackageRoomType
  otherAvailability: { [checkIn: string]: boolean }
  selectedDay?: App.CalendarDay
  defaultDayPicker?: boolean
  flightsOnly?: boolean
  contentRef: React.RefObject<HTMLDivElement | null>
  isAnyFlightsActive?: boolean
  bundleOfferId?: string
}

function OfferCalendarDayPicker(props: Props) {
  const {
    daysByMonth,
    className,
    initialMonth,
    otherAvailability,
    room,
    selectedDay,
    flightsOnly,
    contentRef,
    isAnyFlightsActive,
    bundleOfferId,
  } = props
  // always show month selected, previous month and next month
  const initialMonthIndex = useRef(daysByMonth.findIndex((entry) => entry[1][0]?.monthKey === initialMonth)).current
  const [monthsOnDisplay, setMonthsOnDisplay] = useState({ toShow: 3, offset: Math.max(initialMonthIndex - 1, 0) })
  const [previousRef, previousInView] = useInView({ root: contentRef.current, rootMargin: '20px 0px 0px 0px' })
  const [nextRef, nextInView] = useInView({ root: contentRef.current, rootMargin: '0px 0px 100px 0px' })
  const monthElements = useRef<Record<string, HTMLDivElement>>({})
  const scrollRestoreMonth = useRef<typeof monthsOnDisplay | null>(null)
  const intlDateFormatter = useIntlDateFormatter()

  useLayoutEffect(() => {
    const initialMonthToDisplay = daysByMonth[initialMonthIndex]
    if (initialMonthToDisplay) {
      const monthKey = initialMonthToDisplay[1][0].monthKey
      monthElements.current[monthKey]?.scrollIntoView({ behavior: 'auto' })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMonthIndex])

  // We discard the initial 'previous in view' check because we already show
  // an extra month before this one on mount
  useMountedEffect(() => {
    if (previousInView) {
      scrollRestoreMonth.current = monthsOnDisplay
      setMonthsOnDisplay({ toShow: monthsOnDisplay.toShow + 1, offset: monthsOnDisplay.offset - 1 })
    }
  }, [previousInView])

  // mounting layout handles transition from month to day calendar
  useLayoutEffect(() => {
    if (initialMonthIndex > 0) {
      // we need to scroll to the 'middle' month
      const month = daysByMonth[monthsOnDisplay.offset]
      const scrollPosition = monthElements.current[month[1][0].monthKey]?.offsetHeight + 40
      if (scrollPosition) {
        contentRef.current?.scrollTo({ behavior: 'auto', top: scrollPosition })
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // on offset change handles when scrolling up and new month appears
  useLayoutEffect(() => {
    if (scrollRestoreMonth.current !== undefined) {
      const month = daysByMonth[monthsOnDisplay.offset]
      const scroll = monthElements.current[month[1][0].monthKey]?.offsetHeight + 40

      if (scroll) {
        contentRef.current?.scrollTo({ behavior: 'auto', top: scroll })
      }
      scrollRestoreMonth.current = null
    }
  }, [monthsOnDisplay.offset]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (nextInView) {
      setMonthsOnDisplay(monthsOnDisplay => ({ ...monthsOnDisplay, toShow: monthsOnDisplay.toShow + 1 }))
    }
  }, [nextInView])

  const monthPrices = useMemo(() => {
    return arrayToObject(daysByMonth, ([title]) => title, ([_, days]) => {
      const withTotals = days.filter(day => {
        if (flightsOnly && !day.flightCacheActive) {
          // we don't have a price to show if flight cache isn't active and we're only showing flights
          return false
        }
        return day.hotelAvailable
      })
      const hotelFromPrice = withTotals.length ? Math.min(...withTotals.map(d => d.hotelTotal)) : 0
      const packagedFromPrice = withTotals.length ? Math.min(...withTotals.map(d => d.packagedTotal)) : 0

      return {
        hotelFromPrice,
        packagedFromPrice,
      } })
  }, [daysByMonth, flightsOnly])

  const intl = useIntl()
  const [tier] = useCurrentLoyaltyTier()
  const canLoyaltyUpgrade = useUserHasLoyaltyBenefit('hotel_upgrade')

  return (
    <Root className={className}>
      {monthsOnDisplay.offset > 0 && <div ref={previousRef} />}
      <VerticalSpacer gap={40}>
        {take(daysByMonth, monthsOnDisplay.toShow, monthsOnDisplay.offset).map(([key, days, { year, monthIndex, tacticalDays }]) => {
          const fromPrice = isAnyFlightsActive ? monthPrices[key].packagedFromPrice : monthPrices[key].hotelFromPrice
          const title = intlDateFormatter(new Date(year, monthIndex), INTL_LONG_MONTH_LONG_YEAR)
          const hasMonthBundleAdditionAmount = bundleOfferId && days.some(day => day.surcharge > 0)
          return <Calendar
            key={key}
            gap={20}
            ref={(el) => { monthElements.current[days[0].monthKey] = el! }}
          >
            <div>
              <Heading variant="heading3">{title}</Heading>
              {!!fromPrice && !bundleOfferId && <OfferCalendarPriceBlurb
                year={days[0].year}
                month={days[0].month}
                fromPrice={fromPrice}
                hasLoyaltyRoomUpgrade={days.some(day => !!day.loyalty.availableUpgradeTargetsUniqueKeys.room || !!day.loyalty.availableUpgradeTargetsUniqueKeys.package)}
                hasTactical={tacticalDays.length > 0}
              />}
              {isBundleAndSaveV2Enabled() && hasMonthBundleAdditionAmount && <BodyTextBlock variant="small">
                {intl.formatMessage({
                  id: 'lDnBWmthJa8iRWj4i3Z4',
                  defaultMessage: '* Peak period surcharge',
                  description: 'Text indicating peak period surcharge applies in calendar day picker',
                })}
              </BodyTextBlock>}
            </div>
            <OfferCalendarDaysMonth
              days={days}
              tacticalDays={tacticalDays}
              selectedDay={selectedDay}
              otherAvailability={otherAvailability}
              isRoomSelected={!!room}
              flightsOnly={flightsOnly}
              tier={tier}
              canLoyaltyUpgrade={canLoyaltyUpgrade}
            />
          </Calendar>
        })}
      </VerticalSpacer>
      {(monthsOnDisplay.toShow + monthsOnDisplay.offset) < daysByMonth.length && <div ref={nextRef} />}
    </Root>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  return {
    room: state.room,
    bundleOfferId: state.bundleOffer?.id,
    selectedDay: isMultiRoom ? getSelectedOfferPageMultiRoomDay(state, appState) : getSelectedOfferPageDay(appState, state),
    daysByMonth: isMultiRoom ? getMultiRoomCalendarDays(state, appState) : getCalendarDays(appState, state),
    otherAvailability: isMultiRoom ? getMultiRoomDaysOtherAvailability(state, appState) : getDaysOtherAvailability(appState, state),
    isAnyFlightsActive: state.flights,
    open: !!state.activeFilter,
  }
}, OfferCalendarDayPicker, preventRenderWithClosedFilter)
